import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * SGL-428: an agent asks the User a question as a dialog. The agent decides when
 * to ask, the options and its recommendation (the `ask_user` tool); the app only
 * renders what arrives in the tool_call event and sends the answer back as the
 * User's next message. Nothing here knows about specific questions.
 */

export type AskUserKind = "single" | "multi" | "text" | "number" | "date";

export interface AskUserQuestion {
  question: string;
  kind: AskUserKind;
  options: { label: string; description?: string }[];
  recommended: string[];
  why?: string;
  allow_other: boolean;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}

/** The ask_user tool result, if it is a question to show (status "shown"). */
export function parseAskUserResult(result: unknown): AskUserQuestion | null {
  try {
    const r = typeof result === "string" ? JSON.parse(result) : result;
    if (!r || r.status !== "shown" || typeof r.question !== "string") return null;
    const kind = r.kind as AskUserKind;
    if (!["single", "multi", "text", "number", "date"].includes(kind)) return null;
    return {
      question: r.question,
      kind,
      options: Array.isArray(r.options) ? r.options.filter((o: any) => o && typeof o.label === "string") : [],
      recommended: Array.isArray(r.recommended) ? r.recommended : [],
      why: typeof r.why === "string" ? r.why : "",
      allow_other: r.allow_other !== false,
      min: typeof r.min === "number" ? r.min : undefined,
      max: typeof r.max === "number" ? r.max : undefined,
      step: typeof r.step === "number" ? r.step : undefined,
      unit: typeof r.unit === "string" ? r.unit : "",
    };
  } catch {
    return null;
  }
}

interface AskUserContextValue {
  question: AskUserQuestion | null;
  answer: (text: string) => void;
  dismiss: () => void;
}

export const AskUserContext = createContext<AskUserContextValue>({
  question: null,
  answer: () => undefined,
  dismiss: () => undefined,
});

/** Shown above the chat input while the agent is waiting for an answer. */
export const AskUserDialog: React.FC<{ disabled?: boolean }> = ({ disabled = false }) => {
  const { question: q, answer, dismiss } = useContext(AskUserContext);
  const [picked, setPicked] = useState<string[]>([]);
  const [other, setOther] = useState("");
  const [text, setText] = useState("");
  const [num, setNum] = useState<number>(0);

  // Start from the agent's recommendation each time a new question arrives.
  useEffect(() => {
    if (!q) return;
    setPicked(q.kind === "single" || q.kind === "multi" ? q.recommended.slice(0, q.kind === "single" ? 1 : 6) : []);
    setOther("");
    setText("");
    setNum(typeof q.min === "number" ? q.min : 0);
  }, [q]);

  const reply = useMemo(() => {
    if (!q) return "";
    if (q.kind === "single") return other.trim() || picked[0] || "";
    if (q.kind === "multi") return [...picked, ...(other.trim() ? [other.trim()] : [])].join(", ");
    if (q.kind === "number") return `${num}${q.unit ? ` ${q.unit}` : ""}`;
    return text.trim();
  }, [q, picked, other, text, num]);

  if (!q) return null;
  const isRec = (label: string) => q.recommended.includes(label);
  const toggle = (label: string) =>
    setPicked((prev) =>
      q.kind === "single" ? [label] : prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );

  return (
    <section
      aria-label="Question from your agent"
      className="mx-auto mb-3 w-full max-w-3xl rounded-2xl border border-theme-border bg-theme-surface p-4 shadow-sm"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <p className="text-[15px] font-semibold text-theme-fg">{q.question}</p>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Answer in the chat instead"
          title="Answer in the chat instead"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-theme-fg-muted hover:bg-theme-background"
        >
          <span className="material-icons text-[18px]">close</span>
        </button>
      </div>

      {(q.kind === "single" || q.kind === "multi") && (
        <div className="space-y-2" role={q.kind === "single" ? "radiogroup" : "group"}>
          {q.options.map((o) => {
            const on = picked.includes(o.label) && !(q.kind === "single" && other.trim());
            return (
              <label
                key={o.label}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 text-sm ${
                  on ? "border-theme-primary bg-theme-primary/5" : "border-theme-border"
                }`}
              >
                <input
                  type={q.kind === "single" ? "radio" : "checkbox"}
                  name="ask-user-option"
                  checked={on}
                  onChange={() => {
                    setOther("");
                    toggle(o.label);
                  }}
                  className="mt-0.5 h-4 w-4"
                  style={{ accentColor: "#2f4941" }}
                />
                <span className="min-w-0 flex-1">
                  <span className="font-medium text-theme-fg">{o.label}</span>
                  {isRec(o.label) && (
                    <span className="ml-2 rounded-full bg-theme-primary/10 px-2 py-0.5 text-[11px] font-semibold text-theme-primary">
                      Recommended
                    </span>
                  )}
                  {o.description && <span className="mt-0.5 block text-xs text-theme-fg-muted">{o.description}</span>}
                </span>
              </label>
            );
          })}
          {q.allow_other && (
            <input
              type="text"
              value={other}
              onChange={(e) => {
                setOther(e.target.value);
                if (q.kind === "single") setPicked([]);
              }}
              placeholder="Something else…"
              aria-label="Your own answer"
              className="block w-full rounded-xl border border-theme-border bg-transparent px-3 py-2.5 text-sm text-theme-fg outline-none focus:border-theme-primary"
            />
          )}
        </div>
      )}

      {q.kind === "text" && (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          aria-label="Your answer"
          className="block w-full resize-y rounded-xl border border-theme-border bg-transparent px-3 py-2.5 text-sm text-theme-fg outline-none focus:border-theme-primary"
        />
      )}

      {q.kind === "number" && (
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={q.min ?? 0}
            max={q.max ?? 100}
            step={q.step ?? 1}
            value={num}
            onChange={(e) => setNum(Number(e.target.value))}
            aria-label={q.question}
            className="flex-1"
            style={{ accentColor: "#2f4941" }}
          />
          <span className="w-24 text-right text-sm font-semibold text-theme-fg">
            {num} {q.unit}
          </span>
        </div>
      )}

      {q.kind === "date" && (
        <input
          type="date"
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label={q.question}
          className="rounded-xl border border-theme-border bg-transparent px-3 py-2 text-sm text-theme-fg"
        />
      )}

      {q.why && q.recommended.length > 0 && (
        <p className="mt-3 text-xs text-theme-fg-muted">
          <span className="font-semibold">Why recommended:</span> {q.why}
        </p>
      )}

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          disabled={disabled || !reply}
          onClick={() => answer(reply)}
          className="h-10 rounded-xl bg-theme-primary px-5 text-sm font-bold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Send answer
        </button>
      </div>
    </section>
  );
};
